import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import "./ReviewsBar.css";
// Local data file lives alongside this component; no bundler alias needed
import { reviewsByPage } from "./reviewsByPage";

// Simple Fisher–Yates shuffle
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function formatRating(rating) {
  // show 4.5 instead of 4.50
  return Number.isInteger(rating) ? `${rating}` : `${rating}`.replace(/\.0$/, "");
}

// ⭐ display: show full stars + rating text (handles 3.5 cleanly without complex half-star UI)
function Stars({ rating }) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.5;
  const stars = "⭐".repeat(full) + (hasHalf ? "½" : "");
  return (
    <span className="review-stars" aria-label={`${rating} out of 5`}>
      {stars} <span className="review-rating-text">{formatRating(rating)}/5</span>
    </span>
  );
}

/**
 * Find the best review set for this path.
 * - exact match first
 * - then prefix match for dynamic-ish paths
 * - otherwise default
 */
function getReviewsForPath(pathname) {
  if (reviewsByPage[pathname]) return reviewsByPage[pathname];

  // prefix matching helps routes like "/booking-confirmation/ABC123"
  const prefixMatches = Object.keys(reviewsByPage)
    .filter((k) => k !== "default" && k !== pathname && pathname.startsWith(k + "/"));

  if (prefixMatches.length > 0) {
    // choose the longest prefix (most specific)
    prefixMatches.sort((a, b) => b.length - a.length);
    return reviewsByPage[prefixMatches[0]];
  }

  return reviewsByPage.default;
}

const ReviewsBar = ({ pageKey, count = 3, title = "⭐ Customer Reviews", rolling = false, intervalMs = 6000, className = "", style = null }) => {
  const location = useLocation();
  const key = pageKey || location.pathname.split("?")[0] || "/";

  const reviewPool = useMemo(() => {
    const source = getReviewsForPath(key) || reviewsByPage.default;

    // If someone accidentally adds fewer/more than 5, we still behave safely
    const pool = Array.isArray(source) ? source : reviewsByPage.default;

    return shuffle(pool);
  }, [key, count]);

  const [startIndex, setStartIndex] = useState(0);
  const visibleCount = Math.min(count, reviewPool.length);

  useEffect(() => {
    setStartIndex(0);
  }, [reviewPool]);

  useEffect(() => {
    if (!rolling || reviewPool.length <= visibleCount) return undefined;
    const timer = setInterval(() => {
      setStartIndex((current) => (current + visibleCount) % reviewPool.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [rolling, reviewPool.length, visibleCount, intervalMs]);

  const selectedReviews = useMemo(() => {
    if (!rolling || reviewPool.length <= visibleCount) return reviewPool.slice(0, visibleCount);
    return Array.from({ length: visibleCount }, (_, index) => reviewPool[(startIndex + index) % reviewPool.length]);
  }, [rolling, reviewPool, startIndex, visibleCount]);

  return (
    <div className={`reviews-bar ${className}`.trim()} style={style || undefined}>
      <h3>{title}</h3>

      <div className="reviews-container">
        {selectedReviews.map((review, index) => (
          <div className="review-card" key={`${review.name}-${index}`}>
            <p className="review-text">“{review.text}”</p>
            <p className="review-author">- {review.name}</p>
            <Stars rating={review.rating} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewsBar;
